import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const CADET_KEY = "cyberkids_active_cadet_id";

export const getActiveCadetId = () => localStorage.getItem(CADET_KEY) || "";
export const setActiveCadetId = (id) => {
  if (id) localStorage.setItem(CADET_KEY, id);
  else localStorage.removeItem(CADET_KEY);
};
export const clearActiveCadet = () => localStorage.removeItem(CADET_KEY);

export const api = axios.create({ baseURL: API });
api.interceptors.request.use((config) => {
  const id = getActiveCadetId();
  if (id) config.headers["X-Cadet-Id"] = id;
  return config;
});

export const fetchCadet = () => api.get("/cadet").then(r => r.data);
export const updateCadet = (payload) => api.put("/cadet", payload).then(r => r.data);

export const fetchMissions = () => api.get("/missions").then(r => r.data);
export const createMission = (m) => api.post("/missions", m).then(r => r.data);
export const createMissionsBatch = (missions, source = "voice") =>
  api.post("/missions/batch", { missions, source }).then(r => r.data);
export const completeMission = (id) => api.put(`/missions/${id}/complete`).then(r => r.data);
export const deleteMission = (id) => api.delete(`/missions/${id}`).then(r => r.data);

export const ttsSpeak = (text, voice = "fable") =>
  api.post("/tts", { text, voice }).then(r => r.data);

export const sttTranscribe = async (blob, filename = "voice.webm") => {
  const fd = new FormData();
  fd.append("file", blob, filename);
  const r = await api.post("/stt", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return r.data;
};

export const refineSignal = (transcript) =>
  api.post("/refine-signal", { transcript }).then(r => r.data);

export const fetchLore = () => api.get("/lore").then(r => r.data);
export const generateLore = () => api.post("/lore/generate").then(r => r.data);

export const pingTelemetry = (deployment_url) =>
  api.post("/telemetry/ping", { deployment_url }).then(r => r.data);
export const fetchTelemetry = () => api.get("/telemetry").then(r => r.data);

export const fetchLinks = () => api.get("/links").then(r => r.data);
export const createLink = (payload) => api.post("/links", payload).then(r => r.data);
export const deleteLink = (slug) => api.delete(`/links/${slug}`).then(r => r.data);

export const fetchAssessmentBank = () => api.get("/assessment/bank").then(r => r.data);
export const submitAssessment = (answers) =>
  api.post("/assessment/submit", { answers }).then(r => r.data);
export const fetchAssessmentHistory = () =>
  api.get("/assessment/history").then(r => r.data);

// Curriculum
export const fetchCurriculum = () => api.get("/curriculum").then(r => r.data);
export const completeLesson = (moduleId, lessonId) =>
  api.post(`/curriculum/lesson/${moduleId}/${lessonId}/complete`).then(r => r.data);
export const submitQuiz = (moduleId, answers) =>
  api.post(`/curriculum/quiz/${moduleId}/submit`, { answers }).then(r => r.data);
export const fetchBadges = () => api.get("/badges").then(r => r.data);

// Mentors
export const fetchMentors = () => api.get("/mentors").then(r => r.data);
export const createMentor = (payload) => api.post("/mentors", payload).then(r => r.data);
export const fetchMentorMessages = () => api.get("/mentor-messages").then(r => r.data);
export const postMentorMessage = (payload) => api.post("/mentor-messages", payload).then(r => r.data);
export const markMessageRead = (id) => api.put(`/mentor-messages/${id}/read`).then(r => r.data);

// Streak
export const fetchStreak = () => api.get("/streak").then(r => r.data);
export const tickStreak = () => api.post("/streak/tick").then(r => r.data);

// Next-lesson + Legacy
export const fetchNextLesson = () => api.get("/curriculum/next-lesson").then(r => r.data);
export const legacyPromote = () => api.post("/cadet/legacy-promote").then(r => r.data);

// Cadets / Auth
export const fetchAllCadets = () => api.get("/cadets").then(r => r.data);
export const createCadetAccount = (callsign, pin) => api.post("/cadets", { callsign, pin }).then(r => r.data);
export const login = (callsign, pin) => api.post("/auth/login", { callsign, pin }).then(r => r.data);

// Sovereign Sentinel — Bug Bounty Vault
export const fetchBounties = (track) =>
  api.get("/sentinel/bounties", { params: track ? { track } : {} }).then(r => r.data);
export const fetchBountyDetail = (id) =>
  api.get(`/sentinel/bounty/${id}`).then(r => r.data);
export const submitSandbox = (id, selectedLines, tags) =>
  api.post(`/sentinel/sandbox/${id}/submit`, { selected_lines: selectedLines, tags: tags || null }).then(r => r.data);
export const claimSolana = (id, note = "") =>
  api.post(`/sentinel/solana/${id}/claim`, { note }).then(r => r.data);
export const fetchSentinelLeaderboard = (track) =>
  api.get("/sentinel/leaderboard", { params: track ? { track } : {} }).then(r => r.data);
export const fetchThreatmap = () =>
  api.get("/sentinel/threatmap").then(r => r.data);
export const fetchSentinelEarnings = () =>
  api.get("/sentinel/earnings").then(r => r.data);
export const fetchDonationPool = () =>
  api.get("/sentinel/donations").then(r => r.data);

// Weekly report
export const downloadWeeklyReport = async () => {
  const id = getActiveCadetId();
  const headers = id ? { "X-Cadet-Id": id } : {};
  const res = await fetch(`${API}/report/weekly`, { headers });
  if (!res.ok) throw new Error("Report failed");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const cd = res.headers.get("Content-Disposition") || "";
  const m = cd.match(/filename="(.+?)"/);
  a.href = url;
  a.download = m ? m[1] : "sovereign-report.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
